import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import {
  createAdminUser,
  listAdminUsers,
  resetAdminPassword,
  setAdminRole,
  setAdminStatus,
} from '@/api/ops';
import type { AdminUser } from '@/api/opsTypes';
import { ADMIN_ROLES, type AdminRole } from '@/api/types';
import { OneTimeSecretContent } from '@/components/OneTimeSecretContent';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/ui/DataTable';
import { PageHeader } from '@/ui/PageHeader';
import { QueryError } from '@/ui/QueryError';

interface Revealed {
  title: string;
  warning: string;
  secret: string;
}

const HAND_OVER =
  'This password is shown once and cannot be retrieved again. Hand it over securely; the holder must replace it at their first sign-in.';

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({ queryKey: ['admin-users', page], queryFn: () => listAdminUsers(page) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  const fail = (error: unknown) =>
    setActionError(isApiError(error) ? error.message : 'That did not work. Try again.');

  const changeRole = useMutation({
    mutationFn: (v: { id: string; role: AdminRole }) => setAdminRole(v.id, v.role),
    onSuccess: () => (setActionError(null), refresh()),
    onError: fail,
  });
  const changeStatus = useMutation({
    mutationFn: (user: AdminUser) =>
      setAdminStatus(user.id, user.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'),
    onSuccess: () => (setActionError(null), refresh()),
    onError: fail,
  });
  const reset = useMutation({
    mutationFn: (user: AdminUser) => resetAdminPassword(user.id),
    onSuccess: (result) => {
      setActionError(null);
      setRevealed({
        title: `New password for ${result.admin_user.email}`,
        warning: HAND_OVER,
        secret: result.one_time_secret,
      });
      refresh();
    },
    onError: fail,
  });

  const columns: ColumnDef<AdminUser, never>[] = [
    {
      id: 'user',
      header: 'Admin',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {row.original.email}
            {row.original.self && <span className="text-muted-foreground"> (you)</span>}
          </span>
          {row.original.name && (
            <span className="text-xs text-muted-foreground">{row.original.name}</span>
          )}
        </div>
      ),
    },
    {
      id: 'role',
      header: 'Role',
      cell: ({ row }) =>
        row.original.self ? (
          row.original.role
        ) : (
          <Select
            value={row.original.role}
            onValueChange={(role) =>
              changeRole.mutate({ id: row.original.id, role: role as AdminRole })
            }
          >
            <SelectTrigger aria-label={`Role of ${row.original.email}`} className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ADMIN_ROLES.map((role) => (
                <SelectItem key={role} value={role}>
                  {role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span className="flex flex-wrap gap-1">
          <Badge variant={row.original.status === 'ACTIVE' ? 'secondary' : 'outline'}>
            {row.original.status}
          </Badge>
          {row.original.locked && <Badge variant="destructive">Locked</Badge>}
          {row.original.must_change_password && (
            <Badge variant="outline">Must change password</Badge>
          )}
        </span>
      ),
    },
    {
      id: 'last',
      header: 'Last sign-in',
      cell: ({ row }) =>
        row.original.last_login_at
          ? new Date(row.original.last_login_at).toLocaleString()
          : 'Never',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        row.original.self ? null : (
          <span className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => changeStatus.mutate(row.original)}>
              {row.original.status === 'ACTIVE' ? 'Disable' : 'Enable'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => reset.mutate(row.original)}>
              Reset password
            </Button>
          </span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Admin users"
        actions={
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button>New admin</Button>
            </DialogTrigger>
            <CreateAdminDialog
              key={String(createOpen)}
              onCreated={(result) => {
                setCreateOpen(false);
                setRevealed({
                  title: `Account created for ${result.admin_user.email}`,
                  warning: HAND_OVER,
                  secret: result.one_time_secret,
                });
                refresh();
              }}
            />
          </Dialog>
        }
      />

      {actionError && (
        <Alert variant="destructive">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data?.items ?? []}
          getRowId={(row) => row.id}
          isLoading={query.isPending}
          empty="No admin users."
        />
      )}
      {query.data && query.data.total_pages > 1 && (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= query.data.total_pages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      <Dialog open={revealed !== null} onOpenChange={(open) => !open && setRevealed(null)}>
        {revealed && <OneTimeSecretContent {...revealed} onDone={() => setRevealed(null)} />}
      </Dialog>
    </>
  );
}

const createSchema = z.object({
  email: z.string().trim().min(1, 'Email is required.').email('Enter a valid email address.'),
  name: z.string().trim().max(200, 'Name is too long.'),
  role: z.enum(ADMIN_ROLES),
});
type CreateForm = z.infer<typeof createSchema>;

function CreateAdminDialog({
  onCreated,
}: {
  onCreated: (result: Awaited<ReturnType<typeof createAdminUser>>) => void;
}) {
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { email: '', name: '', role: 'READ_ONLY' },
  });

  const create = useMutation({
    mutationFn: (v: CreateForm) =>
      createAdminUser({ email: v.email, name: v.name || null, role: v.role }),
    onSuccess: onCreated,
    onError: (error) => {
      if (!isApiError(error)) return;
      for (const { field, message } of error.fieldErrors) {
        if (field in createSchema.shape) setError(field as keyof CreateForm, { message });
      }
    },
  });
  const general =
    create.isError && isApiError(create.error) && create.error.fieldErrors.length === 0
      ? create.error.message
      : null;

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>New admin user</DialogTitle>
      </DialogHeader>
      {general && (
        <Alert variant="destructive">
          <AlertDescription>{general}</AlertDescription>
        </Alert>
      )}
      <form id="create-admin" noValidate onSubmit={handleSubmit((v) => create.mutate(v))}>
        <FieldGroup>
          <Field data-invalid={Boolean(errors.email)}>
            <FieldLabel htmlFor="admin-email">Email</FieldLabel>
            <Input id="admin-email" type="email" autoFocus {...register('email')} />
            <FieldError errors={[errors.email]} />
          </Field>
          <Field data-invalid={Boolean(errors.name)}>
            <FieldLabel htmlFor="admin-name">Name (optional)</FieldLabel>
            <Input id="admin-name" {...register('name')} />
            <FieldError errors={[errors.name]} />
          </Field>
          <Field>
            <FieldLabel htmlFor="admin-role">Role</FieldLabel>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="admin-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ADMIN_ROLES.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
        </FieldGroup>
      </form>
      <DialogFooter>
        <Button type="submit" form="create-admin" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create account'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
